import { EventSourcePolyfill } from "event-source-polyfill";
import auth from '../configuration/authentication';

export abstract class EventSourceHandler {

    private static readonly HEALTH_CHECK_PERIOD: number = 5 * 1000;
    private static readonly RECONNECTION_DELAYS: number[] = [ 0.5, 2, 5, 10, 20, 30 ];

    private eventSourceName: string;
    private eventSource: EventSource;
    private healthCheckInterval: any;
    private reconnections: number = 0;

    constructor(name: string) {
        this.eventSourceName = name;
    }

    public subscribeTo(config: EventSourceConfiguration): Promise<EventSource> {
        return auth.ensureFreshToken()
                .then((token) => {
                    this.initEventSource({ ...config, token});
                    return Promise.resolve(this.eventSource);
                })
                .catch(() => {
                    // token could not be refreshed, user might not be logged in.
                    return Promise.reject();
                });
    }

    private initEventSource(config: EventSourceConfigurationWithToken) {
        const headers: any = {
            'Authorization': `Bearer ${config.token}`
        };

        if (config.apiKey != null) {
            headers['X-Gravitee-Api-Key'] = config.apiKey;
        }

        this.eventSource = new EventSourcePolyfill(config.url.toString(), { headers });
        this.eventSource.onopen = (e: Event) => this.onOpen(e, config);
        this.eventSource.onmessage = (me: MessageEvent) => this.onMessage(me);
        this.eventSource.onerror = (e: Event) => this.onError(e, config);
    }

    protected onOpen(e: Event, config: EventSourceConfiguration) {
        const that = this;
        this.reconnections = 0;
        this.healthCheckInterval = setInterval(() => that.healthCheck(config), EventSourceHandler.HEALTH_CHECK_PERIOD);
    }

    protected abstract onMessage(me: MessageEvent): void;

    protected onError(e: Event, config: EventSourceConfiguration) {
        console.warn(`[${this.eventSourceName}-es] Connection lost.`);
        this.unsubscribe();

        setTimeout(() => {
            console.info(`[${this.eventSourceName}-es] Reconnecting (${this.reconnections})...`);
            this.subscribeTo(config);
        }, this.getNextReconnectionDelay());
    }

    private getNextReconnectionDelay() {
        const index = Math.min(this.reconnections++, EventSourceHandler.RECONNECTION_DELAYS.length - 1);
        return EventSourceHandler.RECONNECTION_DELAYS[index] * 1000;
    }

    private healthCheck(config: EventSourceConfiguration) {
        if (!this.eventSource) {
            clearInterval(this.healthCheckInterval);
        }

        if (this.eventSource.readyState === this.eventSource.CONNECTING) {
            console.info(`[${this.eventSourceName}-es] Connection in progress...`)

        } else if (this.eventSource.readyState === this.eventSource.CLOSED) {
            clearInterval(this.healthCheckInterval);
            this.eventSource = null;
            console.info(`[${this.eventSourceName}-es] Reconnecting...`);
            this.subscribeTo(config);
        }
    }

    public unsubscribe() {
        clearInterval(this.healthCheckInterval);
        this.eventSource.close();
        this.eventSource = null;
    }
}

export type EventSourceConfiguration = {
    url: URL;
    apiKey?: string;
    apiKeyRef?: string;
}

type EventSourceConfigurationWithToken = EventSourceConfiguration & {
    token: string;
}